// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { BasicTooltip } from '@nivo/tooltip';
import { type PointTooltip, ResponsiveLine } from '@nivo/line';
import { type FC, useMemo } from 'react';
import { ChartContainer } from '@shared/ui-kit/components';
import { useTranslation } from 'react-i18next';

export interface ValueAtRiskProps {
  readonly valueAtRisk: { quantile: number[]; value: number[] };

  readonly renderAssetAllocation?: boolean;
}

const Tooltip: PointTooltip = ({ point }) => {
  const { t } = useTranslation();

  return (
    <BasicTooltip
      id={
        <span>
          {t('common.value')}:&nbsp;
          <strong>{point.data.yFormatted}</strong>
        </span>
      }
      enableChip
      color={point.serieColor}
    />
  );
};

export const ValueAtRisk: FC<ValueAtRiskProps> = ({ valueAtRisk }) => {
  const { t } = useTranslation();
  const data = useMemo(
    () =>
      valueAtRisk.quantile.map((quantile, index) => ({
        x: quantile,
        y: +(valueAtRisk.value[index] * 100).toFixed(2),
      })),
    [valueAtRisk.quantile, valueAtRisk.value],
  );

  const maxValue = useMemo(() => Math.max(...data.map(({ y }) => y)), [data]);

  const percentage = useMemo(() => data?.[94]?.y ?? 0, [data]);

  return (
    <ChartContainer
      header={t('common.value-at-risk')}
      popupName="charts.valueAtRisk"
      tooltip
    >
      <span className="!money-display mb-3.5 inline-flex rounded-lg bg-primary px-4 py-1.5 text-center font-[Poppins] text-[11px] leading-[13px] text-white">
        {t('common.confidence-level', { value: 95 })} - {percentage}%
      </span>
      <div className="h-[380px]">
        <ResponsiveLine
          data={[
            {
              id: 'Value',
              data: data,
            },
          ]}
          colors={'var(--primaryColor)'}
          margin={{ top: 0, right: 20, bottom: 50, left: 50 }}
          xScale={{
            type: 'linear',
            min: 0,
            max: 100,
          }}
          yScale={{
            type: 'linear',
            max: maxValue,
            min: 0,
          }}
          curve="basis"
          axisTop={null}
          axisRight={null}
          markers={[
            {
              axis: 'x',
              value: 95,
              lineStyle: {
                stroke: 'var(--blueGrayMuted)',
                strokeDasharray: '5 5',
              },
            },
          ]}
          theme={{
            axis: {
              legend: {
                text: {
                  fontSize: 12,
                  fontFamily: 'poppins',
                  fill: 'var(--blueGrayMuted)',
                },
              },
              ticks: {
                text: {
                  fontSize: 12,
                  fontFamily: 'poppins',
                  fill: 'var(--blueGrayMuted)',
                },
              },
            },
            legends: {
              text: {
                textAlign: 'center',
                fontFamily: 'Poppins',
                fontSize: '11px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '13px',
              },
            },
          }}
          lineWidth={2}
          tooltip={Tooltip}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            legend: t('common.value-at-risk-percentage'),
            legendOffset: -44,
            legendPosition: 'middle',
            tickValues: 4,
          }}
          axisBottom={{
            legend: t('common.confidence-level-percentage'),
            legendOffset: 44,
            legendPosition: 'middle',
            tickSize: 5,
            tickPadding: 5,
            tickValues: [0, 50, 100],
          }}
          enableGridX={false}
          enableGridY
          enablePoints={false}
          useMesh={true}
          animate={false}
        />
      </div>
    </ChartContainer>
  );
};
